import { TranslateObject } from '@lumapps/translations';
import { LegacyWidget, WidgetTranslatableData } from '@lumapps/widget-base/types';
import {
    WIDGET_CONTRIBUTION_HTML_SERIALIZATION_OPTIONS,
    WIDGET_CONTRIBUTION_TYPE,
} from '@lumapps/widget-contribution/constants';
import { type StructuredContent } from '@lumapps/wrex/document/structured-content';
import { toHTML } from '@lumapps/wrex/serialization/html/toHTML/toHTML';
import { normalizeForSlate } from '@lumapps/wrex/utils/normalizeForSlate';

import { extractWidgetsData } from './parseWidgetsToFormValues';

/** Per-widget dirty field map (structurally matches WidgetsDirtyTextualFields from designer-content-editor). */
export type WidgetsDirtyFields = Record<
    string,
    {
        header?: Record<string, boolean>;
        body?: Record<string, boolean>;
        footer?: Record<string, boolean>;
    }
>;

export const getWidgetsTranslatableData = ({
    sourceLang,
    widgets,
    contentTitle,
    dirtyFields,
}: {
    sourceLang: string;
    contentTitle: TranslateObject<string | null | undefined>;
    widgets: LegacyWidget[];
    /** When provided, shouldAutoTranslate per field is driven by dirty state instead of value presence. */
    dirtyFields?: WidgetsDirtyFields;
}) => {
    if (!sourceLang) {
        return undefined;
    }

    const extractedData = extractWidgetsData({ widgets, contentTitle });
    const result: WidgetTranslatableData = {};

    for (const [uuid, data] of Object.entries(extractedData)) {
        const titleValue = data.title?.[sourceLang];
        let bodyValue = data.body?.translations?.[sourceLang];
        const footerValue = data.footer?.[sourceLang];

        // Serialize contribution rich text to HTML for the translation API.
        if (data.type === WIDGET_CONTRIBUTION_TYPE && bodyValue) {
            const richText = bodyValue as StructuredContent;
            bodyValue = toHTML(normalizeForSlate(richText.children), WIDGET_CONTRIBUTION_HTML_SERIALIZATION_OPTIONS);
        }

        const stringBodyValue = typeof bodyValue === 'string' ? bodyValue : undefined;

        const shouldAutoTranslateTitle = dirtyFields
            ? Boolean(dirtyFields[uuid]?.header?.[sourceLang])
            : Boolean(titleValue);
        const shouldAutoTranslateBody = dirtyFields
            ? Boolean(dirtyFields[uuid]?.body?.[sourceLang])
            : Boolean(stringBodyValue);
        const shouldAutoTranslateFooter = dirtyFields
            ? Boolean(dirtyFields[uuid]?.footer?.[sourceLang])
            : Boolean(footerValue);

        if (shouldAutoTranslateTitle || shouldAutoTranslateBody || shouldAutoTranslateFooter) {
            result[uuid] = {
                title: { valueToTranslate: titleValue, shouldAutoTranslate: shouldAutoTranslateTitle },
                body: { valueToTranslate: stringBodyValue, shouldAutoTranslate: shouldAutoTranslateBody },
                footer: { valueToTranslate: footerValue, shouldAutoTranslate: shouldAutoTranslateFooter },
                type: data.type,
            };
        }
    }

    return result;
};
